import type {
  ApprovalGroupType,
  ApprovalUser,
  SeniorityValues,
} from "@/types/entities/approval-groups";
import axios from "@/utils/axios";
import {
  keepPreviousData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export type ApprovalGroupsFiltersProps = {
  page?: number;
  per_page?: number;
  search?: string;
  status?: string;
  order_by?: string;
  type?: string;
  updated_at?: string;
  trashed?: string;
  user_id?: string;
};

export type ApprovalGroupsApiResponse = {
  data: ApprovalGroupType[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
};

export const useGetApprovalGroups = (
  filters: ApprovalGroupsFiltersProps,
  enable = true,
) => {
  const { page = 1, per_page = 20, ...restFilters } = filters;

  const baseParams: Record<string, string | number> = {
    page,
    per_page,
    ...(restFilters.search && { "filter[search]": restFilters.search }), // Solo incluir si hay texto de búsqueda
    ...(restFilters.order_by && { order_by: restFilters.order_by }),
    ...(restFilters.type && { "filter[type]": restFilters.type }),
    ...(restFilters.updated_at && {
      "filter[updated_at]": restFilters.updated_at,
    }),
    ...(restFilters.user_id && { "filter[user_id]": restFilters.user_id }),
  };

  if (restFilters.trashed && restFilters.trashed !== "active") {
    baseParams["filter[trashed]"] = restFilters.trashed;
  }
  if (restFilters.trashed && restFilters.trashed.split(",").length > 1) {
    baseParams["filter[trashed]"] = "with";
  }

  const queryParams = {
    ...baseParams,
    relationships: "members,activeFlows",
  };

  return useQuery({
    queryKey: ["approval-groups", baseParams],
    queryFn: async (): Promise<ApprovalGroupsApiResponse> => {
      const response = await axios.get("/admin/v1/groups", {
        params: queryParams,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: enable,
  });
};

export const useCreateApprovalGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      description?: string;
      type: string;
    }) => {
      const response = await axios.post("/admin/v1/groups", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["approval-groups"],
      });
    },
  });
};

export const useCreateGroupWorkload = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      seniorityValues,
    }: {
      id: string;
      seniorityValues: SeniorityValues;
    }) => {
      const body = {
        workloads: Object.entries(seniorityValues).map(([seniority, obj]) => {
          const seniorityNum = Number(seniority);
          // Líder (1) y Supervisor (2) siempre tienen amount: 0
          // Solo Técnico (3) puede tener el valor configurado
          const amount =
            seniorityNum === 1 || seniorityNum === 2 ? 0 : obj.amount;
          return {
            seniority: seniorityNum,
            amount,
          };
        }),
      };
      const response = await axios.post(
        `/admin/v1/groups/${id}/workload`,
        body,
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["approval-groups"],
      });
    },
  });
};

export const useUpdateApprovalGroup = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name?: string;
      description?: string;
      type?: string;
    }) => {
      const response = await axios.patch(`/admin/v1/groups/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["approval-groups"],
      });
      queryClient.invalidateQueries({
        queryKey: ["approval-group", id],
      });
    },
  });
};

export const useDeleteApprovalGroup = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await axios.delete(`/admin/v1/groups/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["approval-groups"],
      });
      queryClient.invalidateQueries({
        queryKey: ["approval-group", id],
      });
    },
  });
};

export const useRestoreApprovalGroup = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await axios.post(`/admin/v1/groups/${id}/restore`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["approval-groups"],
      });
      queryClient.invalidateQueries({
        queryKey: ["approval-group", id],
      });
    },
  });
};

export const useSyncMembersToGroup = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      members: Array<{
        id: string;
        level: number;
        priority: number;
        seniority: number;
      }>;
      groupId?: string;
    }) => {
      const groupId = body.groupId || id; // Use provided groupId or fall back to hook id
      const response = await axios.post(`/admin/v1/groups/${groupId}/users`, {
        members: body.members,
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      const groupId = variables.groupId || id;
      queryClient.invalidateQueries({
        queryKey: ["approval-groups"],
      });
      queryClient.invalidateQueries({
        queryKey: ["approval-group", groupId],
      });
    },
  });
};

export type ApprovalGroupsUsersFiltersProps = {
  page?: number;
  per_page?: number;
  search?: string;
};

export type ApprovalGroupsUsersApiResponse = {
  data: ApprovalUser[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
};

export const useGetMembersForGroup = (
  filters: Omit<ApprovalGroupsUsersFiltersProps, "page">,
  enabled = true,
) => {
  const { per_page = 20, search } = filters;

  const queryParams: Record<string, string | number> = {
    per_page,
    ...(search && { search: search }),
  };

  return useInfiniteQuery({
    queryKey: ["approval-groups-users", queryParams],
    queryFn: async ({
      pageParam = 1,
    }): Promise<ApprovalGroupsUsersApiResponse> => {
      const response = await axios.get("/admin/v1/groups-users", {
        params: { ...queryParams, page: pageParam },
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = (
        lastPage as ApprovalGroupsUsersApiResponse
      ).meta;
      if (current_page < last_page) return current_page + 1;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled,
  });
};

// New hook to get users assigned to a specific group
export const useGetGroupMembers = (
  groupId: string,
  filters?: { search?: string; page?: number; per_page?: number },
) => {
  const queryParams: Record<string, string | number> = {
    page: filters?.page || 1,
    per_page: filters?.per_page || 20,
    ...(filters?.search && { search: filters.search }),
  };

  return useQuery({
    queryKey: ["group-members", groupId, queryParams],
    queryFn: async (): Promise<ApprovalGroupsUsersApiResponse> => {
      const response = await axios.get("/admin/v1/groups-users", {
        params: { ...queryParams, group_id: groupId },
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: !!groupId, // Only run query if groupId is provided
  });
};

const getApprovalGroup = async (id: string) => {
  const response = await axios.get(`/admin/v1/groups/${id}`, {
    params: {
      relationships: "members,workloads",
    },
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

export const useGetApprovalGroup = (id: string) => {
  return useQuery({
    queryKey: ["approval-group", id],
    queryFn: async (): Promise<ApprovalGroupType> => {
      const { data } = await getApprovalGroup(id);
      return data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

export const createApprovalGroupDetailsBreadcrumb = (
  queryClient: QueryClient,
  id: string,
) => {
  return async () => {
    const groupId = id;
    try {
      const group = await queryClient.ensureQueryData({
        queryKey: ["approval-group", groupId],
        queryFn: async () => {
          const response = await getApprovalGroup(groupId);
          return response.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
      return {
        data: {
          ...group,
          name: group.name,
        },
      };
    } catch (error) {
      console.error("Error fetching approval group for breadcrumb:", error);
      return {
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };
};

export const useGetApprovalGroupsByUser = (userId: string) => {
  return useMutation({
    mutationFn: async (): Promise<ApprovalGroupsApiResponse> => {
      const response = await axios.get("/admin/v1/groups", {
        params: {
          "filter[user_id]": userId,
          per_page: 100, // Assuming a user won't be in more than 100 groups
          relationships: "members",
        },
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      return response.data;
    },
  });
};

export interface GroupsAvailableMembers {
  group_id: string;
  group_name: string;
  members: Array<{
    id: string;
    name: string;
    last_name: string;
    email: string;
  }>;
}

export const useGetAvailableMembers = (
  formalityId?: string | null,
  groupIds?: string | null,
) => {
  return useQuery({
    queryKey: ["approval-group", formalityId, groupIds],
    queryFn: async (): Promise<GroupsAvailableMembers[]> => {
      const { data } = await getAvailableMembers(formalityId, groupIds);
      return data.data;
    },
    enabled: !!formalityId && !!groupIds,
    refetchOnWindowFocus: false,
  });
};

const getAvailableMembers = (
  formalityId?: string | null,
  groupIds?: string | null,
) => {
  return axios.get(
    `/admin/v1/formality-request/${formalityId}/groups/available-members`,
    {
      params: {
        group_ids: [groupIds],
      },
    },
  );
};
